import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { WorkspaceInfo } from '../contexts/WorkspaceContext';
import { orpc } from '../lib/orpc';

interface UseAuthReturn {
  user: { id: string; githubLogin: string; githubName: string | null; githubAvatarUrl: string | null } | null;
  workspace: WorkspaceInfo | null;
  workspaces: WorkspaceInfo[];
  isOwner: boolean;
  isAdmin: boolean;
  loading: boolean;
  error: boolean;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(orpc.auth.me.queryOptions({ input: {} }));

  const logout = useCallback(async (): Promise<void> => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      // Immediately mark user as logged out
      queryClient.setQueryData(orpc.auth.me.queryOptions({ input: {} }).queryKey, {
        user: null,
        workspace: null,
        workspaces: [],
      });
      // Wipe all other cached data to prevent stale session data leakage
      void queryClient.invalidateQueries();
    }
  }, [queryClient]);

  return {
    user: data?.user ?? null,
    workspace: data?.workspace ?? null,
    workspaces: data?.workspaces ?? [],
    isOwner: isLoading ? true : data?.workspace?.role === 'owner',
    isAdmin: isLoading ? true : data?.workspace?.role === 'owner' || data?.workspace?.role === 'admin',
    loading: isLoading,
    error: isError,
    logout,
  };
}
